import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Header, SpaceBetween, Box, Button, FormField, Input,
  Select, Textarea, Alert, TokenGroup, Table, ColumnLayout,
  Spinner,
} from "@cloudscape-design/components";
import { UserInfo, SiteConfig, ShiftTypeConfig } from "../types";
import { apiClient } from "../api/client";

interface Props { user: UserInfo; siteId: string; }

const DEFAULT_SHIFT_TYPES: ShiftTypeConfig[] = [
  { id: "day", name: "Day Shift", color: "#FF9900" },
  { id: "night", name: "Night Shift", color: "#0972D3" },
  { id: "morning", name: "Morning Window", color: "#7B2F8E" },
];

const DAY_OPTIONS = [
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
  { value: "Sunday", label: "Sunday" },
];

export const SiteAdmin: React.FC<Props> = ({ user, siteId }) => {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Trades state
  const [trades, setTrades] = useState<string[]>([]);
  const [newTrade, setNewTrade] = useState("");

  // Shift types state
  const [shiftTypes, setShiftTypes] = useState<ShiftTypeConfig[]>(DEFAULT_SHIFT_TYPES);
  const [newShiftName, setNewShiftName] = useState("");
  const [newShiftColor, setNewShiftColor] = useState("#FF9900");

  // Call settings
  const [callDay, setCallDay] = useState("Thursday");
  const [callTime, setCallTime] = useState("");
  const [zoomId, setZoomId] = useState("");
  const [inviteList, setInviteList] = useState("");

  // Tenets
  const [tenets, setTenets] = useState("");

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/sites?siteId=${siteId}`);
      const site = data.site as SiteConfig;
      if (site) {
        setConfig(site);
        setTrades(site.trades || []);
        setShiftTypes(site.shiftTypes && site.shiftTypes.length > 0 ? site.shiftTypes : DEFAULT_SHIFT_TYPES);
        setCallDay(site.callDay || "Thursday");
        setCallTime(site.callTime || "");
        setZoomId(site.zoomId || "");
        setInviteList(site.inviteList || "");
        setTenets(site.tenets || "");
      }
    } catch (err) {
      console.error("Failed to fetch site config:", err);
      setErrorMsg("Failed to load site configuration");
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async (section: string, updates: Record<string, any>) => {
    setSaving(true);
    setSuccessMsg("");
    setErrorMsg("");
    try {
      await apiClient.put(`/sites/${siteId}`, updates);
      setSuccessMsg(`${section} saved successfully`);
      setTimeout(() => setSuccessMsg(""), 3000);
      await fetchConfig();
    } catch (err: any) {
      setErrorMsg(`Failed to save ${section}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addTrade = () => {
    const t = newTrade.trim();
    if (t && !trades.includes(t)) {
      setTrades(prev => [...prev, t]);
      setNewTrade("");
    }
  };

  const removeTrade = (index: number) => {
    setTrades(prev => prev.filter((_, i) => i !== index));
  };

  const addShiftType = () => {
    const name = newShiftName.trim();
    if (name) {
      const id = name.toLowerCase().replace(/\s+/g, "-");
      if (!shiftTypes.find(s => s.id === id)) {
        setShiftTypes(prev => [...prev, { id, name, color: newShiftColor }]);
        setNewShiftName("");
        setNewShiftColor("#FF9900");
      }
    }
  };

  const removeShiftType = (id: string) => {
    setShiftTypes(prev => prev.filter(s => s.id !== id));
  };

  const updateShiftColor = (id: string, color: string) => {
    setShiftTypes(prev => prev.map(s => s.id === id ? { ...s, color } : s));
  };

  const moveShiftUp = (id: string) => {
    const idx = shiftTypes.findIndex(s => s.id === id);
    if (idx <= 0) return;
    setShiftTypes(prev => { const arr = [...prev]; [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]; return arr; });
  };

  const moveShiftDown = (id: string) => {
    const idx = shiftTypes.findIndex(s => s.id === id);
    if (idx < 0 || idx >= shiftTypes.length - 1) return;
    setShiftTypes(prev => { const arr = [...prev]; [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]; return arr; });
  };

  if (loading) {
    return <Box textAlign="center" padding={{ top: "xxxl" }}><Spinner size="large" /></Box>;
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description={`Configuration for ${siteId}`}>
        Site Admin
      </Header>

      {successMsg && <Alert type="success" dismissible onDismiss={() => setSuccessMsg("")}>{successMsg}</Alert>}
      {errorMsg && <Alert type="error" dismissible onDismiss={() => setErrorMsg("")}>{errorMsg}</Alert>}

      {/* Trades Section */}
      <Container header={<Header variant="h2" description="Configure which trade types are available for maintenance events" actions={<Button onClick={() => handleSave("Trades", { trades })} loading={saving}>Save Trades</Button>}>Trades</Header>}>
        <SpaceBetween size="m">
          <TokenGroup
            items={trades.map((t, i) => ({ label: t, dismissLabel: `Remove ${t}` }))}
            onDismiss={({ detail }) => removeTrade(detail.itemIndex)}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <FormField label="Add Trade">
              <Input
                value={newTrade}
                onChange={({ detail }) => setNewTrade(detail.value)}
                placeholder="e.g. RME"
                onKeyDown={({ detail }) => { if (detail.key === "Enter") addTrade(); }}
              />
            </FormField>
            <Button onClick={addTrade} iconName="add-plus">Add</Button>
          </div>
        </SpaceBetween>
      </Container>

      {/* Shift Types Section */}
      <Container header={<Header variant="h2" description="Configure shift types, colours, and display order on the calendar" actions={<Button onClick={() => handleSave("Shift Types", { shiftTypes })} loading={saving}>Save Shift Types</Button>}>Shift Types</Header>}>
        <SpaceBetween size="m">
          <Table
            items={shiftTypes}
            columnDefinitions={[
              {
                id: "order",
                header: "Order",
                width: 100,
                cell: item => {
                  const idx = shiftTypes.findIndex(s => s.id === item.id);
                  return (
                    <SpaceBetween direction="horizontal" size="xxxs">
                      <Button variant="icon" iconName="angle-up" onClick={() => moveShiftUp(item.id)} disabled={idx === 0} />
                      <Button variant="icon" iconName="angle-down" onClick={() => moveShiftDown(item.id)} disabled={idx === shiftTypes.length - 1} />
                    </SpaceBetween>
                  );
                },
              },
              {
                id: "name",
                header: "Shift Name",
                cell: item => item.name,
              },
              {
                id: "color",
                header: "Colour",
                cell: item => (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="color"
                      value={item.color}
                      onChange={(e) => updateShiftColor(item.id, e.target.value)}
                      style={{ width: 36, height: 28, border: "none", cursor: "pointer" }}
                    />
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>{item.color}</span>
                  </div>
                ),
              },
              {
                id: "preview",
                header: "Preview",
                cell: item => (
                  <div style={{ backgroundColor: item.color, color: "#fff", padding: "4px 12px", borderRadius: 4, fontSize: 12, display: "inline-block" }}>
                    {item.name}
                  </div>
                ),
              },
              {
                id: "actions",
                header: "Actions",
                cell: item => (
                  <Button variant="icon" iconName="remove" onClick={() => removeShiftType(item.id)} />
                ),
              },
            ]}
            empty={<Box textAlign="center" color="inherit">No shift types configured</Box>}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <FormField label="Shift Name">
              <Input value={newShiftName} onChange={({ detail }) => setNewShiftName(detail.value)} placeholder="e.g. Twilight" />
            </FormField>
            <FormField label="Colour">
              <input
                type="color"
                value={newShiftColor}
                onChange={(e) => setNewShiftColor(e.target.value)}
                style={{ width: 44, height: 32, border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
              />
            </FormField>
            <Button onClick={addShiftType} iconName="add-plus">Add</Button>
          </div>
        </SpaceBetween>
      </Container>

      {/* Call Settings Section */}
      <Container header={<Header variant="h2" description="Weekly call configuration" actions={<Button onClick={() => handleSave("Call Settings", { callDay, callTime, zoomId, inviteList })} loading={saving}>Save Call Settings</Button>}>Call Settings</Header>}>
        <ColumnLayout columns={2}>
          <FormField label="Call Day">
            <Select
              selectedOption={DAY_OPTIONS.find(o => o.value === callDay) || DAY_OPTIONS[3]}
              options={DAY_OPTIONS}
              onChange={({ detail }) => setCallDay(detail.selectedOption.value!)}
            />
          </FormField>
          <FormField label="Call Time">
            <Input value={callTime} onChange={({ detail }) => setCallTime(detail.value)} placeholder="e.g. 10:00" type="text" />
          </FormField>
          <FormField label="Zoom Meeting ID">
            <Input value={zoomId} onChange={({ detail }) => setZoomId(detail.value)} placeholder="e.g. 123-456-7890" />
          </FormField>
        </ColumnLayout>
        <FormField label="Invite List (one alias per line)">
          <Textarea
            value={inviteList}
            onChange={({ detail }) => setInviteList(detail.value)}
            rows={4}
            placeholder={"jnathon\ndanihit\nbobsmith"}
          />
        </FormField>
      </Container>

      {/* Tenets Section */}
      <Container header={<Header variant="h2" description="Team tenets displayed on the Information page" actions={<Button onClick={() => handleSave("Tenets", { tenets })} loading={saving}>Save Tenets</Button>}>Tenets</Header>}>
        <FormField label="Tenets">
          <Textarea
            value={tenets}
            onChange={({ detail }) => setTenets(detail.value)}
            rows={8}
            placeholder="Enter team tenets and principles..."
          />
        </FormField>
      </Container>
    </SpaceBetween>
  );
};
